import React from 'react';
import BaseTable from '@app/components/common/Table/BaseTable';
import { CustomerLedgerTrans, CustomerModel } from '@app/interfaces/customer';
import { Space, Select, Input, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { formatDateTime, formatMoney } from '@app/utils/utils';
import NumberFormatRender from '@app/components/common/NumberFormatRender';
import { Link } from 'react-router-dom';
import { DayjsDatePicker } from '@app/components/common/pickers/DayjsDatePicker';
import dayjs from 'dayjs';
import TransactionTypeTag from '@app/components/common/TransactionType/TransactionTypeTag';
import {
  transactionTypeName,
  transactionTypeSelectOptions,
} from '@app/components/common/TransactionType/transactionTypes';
import { dayjsPresetRanges } from '@app/components/common/pickers/datePresets';
import { DATE_FORMAT } from '@app/constants/dateFormats';

interface CustomerInfoProps {
  record: CustomerModel;
}

const LEDGER_TYPE_OPTIONS = transactionTypeSelectOptions(['all', 'Order', 'Return-Order', 'Ledger']);

const LedgerTransTable: React.FC<CustomerInfoProps> = ({ record }) => {
  const data = record?.ledger_trans || [];

  const [typeFilter, setTypeFilter] = React.useState('all');
  const [voucherSearch, setVoucherSearch] = React.useState('');
  const [dateRange, setDateRange] = React.useState<[string | null, string | null]>([null, null]);

  const { RangePicker } = DayjsDatePicker;

  const filteredData = React.useMemo(() => {
    return data.filter((item) => {
      const matchType = typeFilter === 'all' || item.ledgerable_type === typeFilter;
      const matchVoucher = voucherSearch ? item.voucher?.toLowerCase().includes(voucherSearch.toLowerCase()) : true;
      let matchDate = true;
      if (dateRange[0] && dateRange[1]) {
        const d = new Date(item.trans_date);
        matchDate = d >= new Date(dateRange[0]) && d <= new Date(dateRange[1]);
      }
      return matchType && matchVoucher && matchDate;
    });
  }, [data, typeFilter, voucherSearch, dateRange]);

  const handleExport = () => {
    const headers = ['Mã phiếu', 'Thời gian', 'Loại giao dịch', 'Giá trị', 'Dự nợ khách hàng'];
    const rows = filteredData.map((item) => [
      item.voucher,
      formatDateTime(item.trans_date, 'date'),
      transactionTypeName(item.ledgerable_type),
      item.amount,
      item.debit_amount,
    ]);
    const csvContent = [headers, ...rows].map((row) => row.map((val) => `"${val ?? ''}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `lich-su-giao-dich-${record.customer_code}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<CustomerLedgerTrans> = [
    {
      title: 'Mã phiếu',
      dataIndex: 'voucher',
      key: 'voucher',
      render: (value: any, rec: any) => {
        switch (rec.ledgerable_type) {
          case 'Ledger':
            return (
              <Link to={`/ledgers/${rec.ledgerable_id}`} target="_blank">
                {value}
              </Link>
            );
          case 'Order':
            return (
              <Link to={`/orders/${rec.ledgerable_id}`} target="_blank">
                {value}
              </Link>
            );
          case 'Return-Order':
            return (
              <Link to={`/order-returns/${rec.ledgerable_id}`} target="_blank">
                {value}
              </Link>
            );
          default:
            return value;
        }
      },
    },
    {
      title: 'Thời gian',
      dataIndex: 'trans_date',
      key: 'trans_date',
      render: (value: any) => formatDateTime(value, 'date'),
    },
    {
      title: 'Loại giao dịch',
      dataIndex: 'ledgerable_type',
      key: 'ledgerable_type',
      render: (value: any) => <TransactionTypeTag value={value} />,
    },
    {
      title: 'Giá trị',
      dataIndex: 'amount',
      key: 'amount',
      render: (value: any) => <NumberFormatRender value={value} />,
    },
    {
      title: 'Dự nợ khách hàng',
      dataIndex: 'debit_amount',
      key: 'debit_amount',
      render: (value: any) => <NumberFormatRender value={value} />,
    },
  ];

  return (
    <div className="px-3">
      <div className="flex-between mb-3 align-items-center">
        <h4 className="mb-0">
          Số lần mua: <span className="text-primary font-bold-weight me-1">{record?.number_of_orders}</span>
          {' | '}
          Tổng bán trừ trả hàng:{' '}
          <span className="text-primary font-bold-weight me-1">
            {formatMoney(record?.total_order_amount)}
          </span>
        </h4>

        <Space size={8}>
          <Input
            placeholder="Tìm mã phiếu"
            value={voucherSearch}
            onChange={(e) => setVoucherSearch(e.target.value)}
            allowClear
            size="small"
            style={{ width: 150, height: 36 }}
          />
          <RangePicker
            format={DATE_FORMAT}
            size="small"
            value={dateRange[0] && dateRange[1] ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : [null, null]}
            onChange={(_: any, dateStrings: string[]) => setDateRange([dateStrings[0] || null, dateStrings[1] || null])}
            style={{ width: 220 }}
            ranges={dayjsPresetRanges()}
          />
          <Select
            size="small"
            style={{ width: 170 }}
            value={typeFilter}
            options={LEDGER_TYPE_OPTIONS}
            onChange={setTypeFilter}
          />
          <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={handleExport} title="Xuất CSV">
            Export
          </Button>
        </Space>
      </div>

      <BaseTable rowKey="voucher" columns={columns} dataSource={filteredData} size="small" />
    </div>
  );
};

export default LedgerTransTable;
